import {
  ARENA_BOUNDS,
  BALL_RADIUS,
  DEFAULT_LEVEL_PARAMETERS,
  LEVEL_DURATION_MS,
  PHYSICS,
  SCORE_POINTS_PER_SECOND,
  chargeToTextColor,
} from "./config";
import type { ArenaBounds, WallColor } from "./config";
import {
  canLoadLevelId,
  getLevelDefinition,
  getNextLevelId,
  getPreviousLevelId,
  seedForLevel,
  type LevelId,
  type LevelReference,
} from "./levels";
import {
  getPerkDefinition,
  PERK_DEFINITIONS,
  PERK_FREEZE_DURATION_MS,
  type PerkId,
} from "./perks";
import { tracePolarity } from "./trace";

export type Charge = -1 | 1;
export type SimulationStatus = "running" | "paused" | "won" | "lost";

interface BallState {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  charge: Charge;
}

export interface BallSnapshot {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  charge: Charge;
  color: string;
}

export interface SimulationSnapshot {
  coordinateSystem: string;
  level: LevelId;
  status: SimulationStatus;
  arena: ArenaBounds;
  levelDurationMs: number;
  forceStrength: number;
  bounce: number;
  timerRemainingMs: number;
  timerRemainingSeconds: number;
  score: {
    totalPoints: number;
    earnedTotalPoints: number;
    carriedPoints: number;
    currentLevelPoints: number;
    spentPoints: number;
    pointsPerSecond: number;
  };
  perks: {
    shop: PerkShopItemSnapshot[];
    inventory: PerkInventoryItemSnapshot[];
    activeEffects: {
      freezeBallsRemainingMs: number;
      wallsNeutralized: boolean;
    };
  };
  ballCount: number;
  walls: {
    left: { charge: Charge | null; color: WallColor };
    right: { charge: Charge | null; color: WallColor };
    top: { charge: Charge | null; color: WallColor };
    bottom: { charge: Charge | null; color: WallColor };
  };
  balls: BallSnapshot[];
  banner: string | null;
  hint: string;
}

interface CollisionPair {
  aId: number;
  bId: number;
  aCharge: Charge;
  bCharge: Charge;
  aX: number;
  aY: number;
  bX: number;
  bY: number;
  distance: number;
  threshold: number;
}

const FLIP_COLLISION_GUARD_WINDOW_MS = 250;
const FLIP_COLLISION_GUARD_STEPS = Math.max(
  1,
  Math.ceil(FLIP_COLLISION_GUARD_WINDOW_MS / PHYSICS.fixedStepMs),
);
const TIME_FLOAT_TOLERANCE_MS = 0.0001;

export class PolaritySimulation {
  private balls: BallState[] = [];
  private level: LevelId = "1";
  private arena: ArenaBounds = { ...ARENA_BOUNDS };
  private status: SimulationStatus = "running";
  private levelDurationMs = LEVEL_DURATION_MS;
  private forceStrength = DEFAULT_LEVEL_PARAMETERS.forceStrength;
  private bounce = DEFAULT_LEVEL_PARAMETERS.bounce;
  private timerRemainingMs = LEVEL_DURATION_MS;
  private carriedScorePoints = 0;
  private currentLevelElapsedMs = 0;
  private spentScorePoints = 0;
  private currentLevelScoreTransferable = false;
  private perkInventory: Record<PerkId, number> = createEmptyPerkInventory();
  private freezeBallsRemainingMs = 0;
  private wallsNeutralizedForAttempt = false;
  private perkRandomState = 1;
  private collidedBallIds = new Set<number>();
  private stepCount = 0;
  private walls = {
    left: DEFAULT_LEVEL_PARAMETERS.leftWallColor,
    right: DEFAULT_LEVEL_PARAMETERS.rightWallColor,
    top: DEFAULT_LEVEL_PARAMETERS.topWallColor,
    bottom: DEFAULT_LEVEL_PARAMETERS.bottomWallColor,
  };

  constructor(level: LevelReference = 1) {
    this.arena = { ...ARENA_BOUNDS };
    this.loadLevel(level);
  }

  step(deltaMs: number): void {
    if (this.status !== "running") {
      return;
    }

    this.stepCount += 1;
    if (this.freezeBallsRemainingMs > TIME_FLOAT_TOLERANCE_MS) {
      this.freezeBallsRemainingMs = Math.max(0, this.freezeBallsRemainingMs - deltaMs);
    } else {
      const dt = deltaMs / 1000;
      this.applyForces(dt);
      this.integratePositions(dt);

      if (this.resolvePendingCollision("step")) {
        return;
      }
    }

    const survivedMs = Math.min(deltaMs, this.timerRemainingMs);
    this.currentLevelElapsedMs = Math.min(
      this.levelDurationMs,
      this.currentLevelElapsedMs + survivedMs,
    );
    this.timerRemainingMs = Math.max(0, this.timerRemainingMs - deltaMs);
    if (this.timerRemainingMs <= TIME_FLOAT_TOLERANCE_MS) {
      this.timerRemainingMs = 0;
      this.status = "won";
      this.currentLevelScoreTransferable = true;
      tracePolarity("level_won", {
        level: this.level,
        stepCount: this.stepCount,
        timerRemainingMs: round(this.timerRemainingMs),
        score: this.buildScoreSnapshot(),
      });
    }
  }

  flipBall(index: number): boolean {
    if (this.status !== "running") {
      tracePolarity("flip_rejected_status", {
        stepCount: this.stepCount,
        ballIndex: index,
        status: this.status,
      });
      return false;
    }

    if (this.resolvePendingCollision("flip_guard")) {
      tracePolarity("flip_rejected_pending_collision", {
        stepCount: this.stepCount,
        ballIndex: index,
      });
      return false;
    }

    const ball = this.balls[index];
    if (!ball) {
      tracePolarity("flip_rejected_missing_ball", {
        stepCount: this.stepCount,
        ballIndex: index,
      });
      return false;
    }

    if (this.collidedBallIds.has(ball.id)) {
      tracePolarity("flip_rejected_collided_ball", {
        stepCount: this.stepCount,
        ballIndex: index,
        ballId: ball.id,
      });
      return false;
    }

    const imminentCollision =
      this.freezeBallsRemainingMs > TIME_FLOAT_TOLERANCE_MS
        ? null
        : this.predictImminentCollisionAfterFlip(index, ball.id);
    if (imminentCollision !== null) {
      tracePolarity("flip_rejected_imminent_collision", {
        stepCount: this.stepCount,
        ballIndex: index,
        ballId: ball.id,
        guardWindowMs: FLIP_COLLISION_GUARD_WINDOW_MS,
        lookaheadSteps: imminentCollision.lookaheadSteps,
        collisions: imminentCollision.collisions.map((collision) => ({
          ...collision,
          distance: round(collision.distance),
          threshold: round(collision.threshold),
        })),
      });
      return false;
    }

    const fromCharge = ball.charge;
    ball.charge = invertCharge(ball.charge);
    tracePolarity("flip_applied", {
      stepCount: this.stepCount,
      ballIndex: index,
      ballId: ball.id,
      fromCharge,
      toCharge: ball.charge,
    });
    return true;
  }

  findBallIndexAt(x: number, y: number, hitSlop = 0): number | null {
    let bestIndex: number | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (let index = 0; index < this.balls.length; index += 1) {
      const ball = this.balls[index];
      const dx = ball.x - x;
      const dy = ball.y - y;
      const distanceSq = dx * dx + dy * dy;
      const hitRadius = ball.radius + hitSlop;
      if (distanceSq <= hitRadius * hitRadius && distanceSq < bestDistance) {
        bestDistance = distanceSq;
        bestIndex = index;
      }
    }

    return bestIndex;
  }

  restartLevel(): void {
    this.loadLevel(this.level);
  }

  pauseLevel(): boolean {
    if (this.status !== "running") {
      tracePolarity("pause_rejected_status", {
        level: this.level,
        stepCount: this.stepCount,
        status: this.status,
      });
      return false;
    }

    this.status = "paused";
    tracePolarity("pause_applied", {
      level: this.level,
      stepCount: this.stepCount,
      timerRemainingMs: round(this.timerRemainingMs),
      score: this.buildScoreSnapshot(),
    });
    return true;
  }

  resumeLevel(): boolean {
    if (this.status !== "paused") {
      tracePolarity("resume_rejected_status", {
        level: this.level,
        stepCount: this.stepCount,
        status: this.status,
      });
      return false;
    }

    this.status = "running";
    tracePolarity("resume_applied", {
      level: this.level,
      stepCount: this.stepCount,
      timerRemainingMs: round(this.timerRemainingMs),
      score: this.buildScoreSnapshot(),
    });
    return true;
  }

  togglePause(): boolean {
    if (this.status === "running") {
      return this.pauseLevel();
    }

    if (this.status === "paused") {
      return this.resumeLevel();
    }

    return false;
  }

  purchasePerk(perkId: PerkId): boolean {
    const perk = getPerkDefinition(perkId);
    if (!perk || !canPurchasePerkInStatus(this.status)) {
      tracePolarity("perk_purchase_rejected", {
        level: this.level,
        stepCount: this.stepCount,
        perkId,
        status: this.status,
        reason: perk ? "status" : "unknown_perk",
      });
      return false;
    }

    const score = this.buildScoreSnapshot();
    if (score.totalPoints < perk.cost) {
      tracePolarity("perk_purchase_rejected", {
        level: this.level,
        stepCount: this.stepCount,
        perkId,
        visibleScore: score.totalPoints,
        cost: perk.cost,
        reason: "score",
      });
      return false;
    }

    this.spentScorePoints += perk.cost;
    this.perkInventory[perk.id] += 1;
    tracePolarity("perk_purchased", {
      level: this.level,
      stepCount: this.stepCount,
      perkId: perk.id,
      cost: perk.cost,
      ownedCount: this.perkInventory[perk.id],
      status: this.status,
      score: this.buildScoreSnapshot(),
    });
    return true;
  }

  activatePerk(perkId: PerkId): boolean {
    const perk = getPerkDefinition(perkId);
    const disabledReason = perk ? this.getPerkDisabledReason(perk.id) : "unknown perk";
    if (!perk || disabledReason !== null) {
      tracePolarity("perk_activation_rejected", {
        level: this.level,
        stepCount: this.stepCount,
        perkId,
        status: this.status,
        reason: disabledReason ?? "unknown perk",
      });
      return false;
    }

    this.perkInventory[perk.id] -= 1;
    switch (perk.id) {
      case "freeze_balls":
        this.freezeBallsRemainingMs = Math.max(
          this.freezeBallsRemainingMs,
          PERK_FREEZE_DURATION_MS,
        );
        break;
      case "equalize_polarities":
        for (const ball of this.balls) {
          ball.charge = 1;
        }
        break;
      case "remove_random_ball": {
        const removeIndex = Math.floor(this.nextPerkRandomUnit() * this.balls.length);
        this.balls.splice(removeIndex, 1);
        break;
      }
      case "neutralize_walls":
        this.walls = {
          left: "gray",
          right: "gray",
          top: "gray",
          bottom: "gray",
        };
        this.wallsNeutralizedForAttempt = true;
        break;
    }

    tracePolarity("perk_activated", {
      level: this.level,
      stepCount: this.stepCount,
      perkId: perk.id,
      ownedCount: this.perkInventory[perk.id],
      activeEffects: this.buildActivePerkEffectsSnapshot(),
      ballCount: this.balls.length,
      walls: this.walls,
    });
    return true;
  }

  addScorePoints(amount: number): boolean {
    if (!isValidAdminScoreAmount(amount)) {
      tracePolarity("admin_add_score_rejected", {
        level: this.level,
        stepCount: this.stepCount,
        amount,
        reason: "invalid_amount",
      });
      return false;
    }

    this.carriedScorePoints += amount;
    tracePolarity("admin_add_score_applied", {
      level: this.level,
      stepCount: this.stepCount,
      amount,
      score: this.buildScoreSnapshot(),
    });
    return true;
  }

  forcePassLevel(): boolean {
    if (this.status === "won") {
      tracePolarity("admin_force_pass_rejected", {
        level: this.level,
        stepCount: this.stepCount,
        status: this.status,
      });
      return false;
    }

    const previousStatus = this.status;
    this.status = "won";
    this.timerRemainingMs = 0;
    this.currentLevelElapsedMs = 0;
    this.currentLevelScoreTransferable = false;
    this.freezeBallsRemainingMs = 0;
    this.collidedBallIds.clear();
    tracePolarity("admin_force_pass_applied", {
      level: this.level,
      stepCount: this.stepCount,
      previousStatus,
    });
    return true;
  }

  skipLevel(): boolean {
    const nextLevelId = getNextLevelId(this.level);
    if (!nextLevelId) {
      return false;
    }
    const fromLevel = this.level;
    this.loadLevel(nextLevelId);
    tracePolarity("admin_skip_level_applied", {
      fromLevel,
      toLevel: this.level,
    });
    return true;
  }

  previousLevel(): boolean {
    const previousLevelId = getPreviousLevelId(this.level);
    if (!previousLevelId) {
      return false;
    }
    const fromLevel = this.level;
    this.loadLevel(previousLevelId);
    tracePolarity("admin_previous_level_applied", {
      fromLevel,
      toLevel: this.level,
    });
    return true;
  }

  jumpToLevel(level: LevelReference): boolean {
    if (!canLoadLevelId(level)) {
      tracePolarity("admin_jump_level_rejected", {
        currentLevel: this.level,
        requestedLevel: level,
      });
      return false;
    }

    const fromLevel = this.level;
    this.loadLevel(level);
    tracePolarity("admin_jump_level_applied", {
      fromLevel,
      toLevel: level,
    });
    return true;
  }

  nextLevel(): boolean {
    if (this.status !== "won") {
      return false;
    }

    const nextLevelId = getNextLevelId(this.level);
    if (!nextLevelId) {
      return false;
    }

    if (this.currentLevelScoreTransferable) {
      this.carriedScorePoints += this.getCurrentLevelScorePoints();
    }
    this.loadLevel(nextLevelId);
    return true;
  }

  getSnapshot(): SimulationSnapshot {
    const score = this.buildScoreSnapshot();
    return {
      coordinateSystem:
        "Canonical fixed arena world. Origin at the top-left of the playfield; +x moves right and +y moves down.",
      level: this.level,
      status: this.status,
      arena: this.arena,
      levelDurationMs: this.levelDurationMs,
      forceStrength: this.forceStrength,
      bounce: roundToHundredths(this.bounce),
      timerRemainingMs: Math.ceil(this.timerRemainingMs),
      timerRemainingSeconds: Number((this.timerRemainingMs / 1000).toFixed(1)),
      score,
      perks: this.buildPerksSnapshot(score),
      ballCount: this.balls.length,
      walls: {
        left: buildWallSnapshot(this.walls.left),
        right: buildWallSnapshot(this.walls.right),
        top: buildWallSnapshot(this.walls.top),
        bottom: buildWallSnapshot(this.walls.bottom),
      },
      balls: this.balls.map((ball) => ({
        id: ball.id,
        x: round(ball.x),
        y: round(ball.y),
        vx: round(ball.vx),
        vy: round(ball.vy),
        radius: ball.radius,
        charge: ball.charge,
        color: chargeToTextColor(ball.charge),
      })),
      banner:
        this.status === "won"
          ? "Level cleared"
          : this.status === "lost"
            ? "Collision detected"
            : this.status === "paused"
              ? "Paused"
              : null,
      hint:
        this.status === "won"
          ? "Press Enter or Space to advance."
          : this.status === "lost"
            ? "Press R, Enter, or Space to retry."
            : this.status === "paused"
              ? "Press Space, P, Escape, or Resume to continue."
              : "Click any ball to flip its charge.",
    };
  }

  renderToText(): string {
    return JSON.stringify(this.getSnapshot());
  }

  private loadLevel(level: LevelReference): void {
    const definition = getLevelDefinition(level);
    this.level = definition.level;
    this.status = "running";
    this.levelDurationMs = definition.durationMs;
    this.forceStrength = definition.forceStrength;
    this.bounce = definition.bounce;
    this.timerRemainingMs = definition.durationMs;
    this.currentLevelElapsedMs = 0;
    this.currentLevelScoreTransferable = false;
    this.freezeBallsRemainingMs = 0;
    this.wallsNeutralizedForAttempt = false;
    this.perkRandomState = createPerkSeed(definition.level);
    this.collidedBallIds.clear();
    this.stepCount = 0;
    this.walls = definition.walls;
    this.balls = definition.balls.map((ball, index) => ({
      id: index + 1,
      x: ball.x,
      y: ball.y,
      vx: ball.vx,
      vy: ball.vy,
      radius: BALL_RADIUS,
      charge: ball.charge,
    }));
    tracePolarity("level_loaded", {
      level: this.level,
      forceStrength: this.forceStrength,
      bounce: roundToHundredths(this.bounce),
      wallColors: this.walls,
      balls: this.balls.map((ball) => ({
        id: ball.id,
        charge: ball.charge,
        x: round(ball.x),
        y: round(ball.y),
        vx: round(ball.vx),
        vy: round(ball.vy),
      })),
    });
  }

  private applyForces(dt: number): void {
    this.applyForcesToBalls(this.balls, dt);
  }

  private applyForcesToBalls(balls: BallState[], dt: number): void {
    const deltaVx = new Array(balls.length).fill(0);
    const deltaVy = new Array(balls.length).fill(0);
    const softeningSq = PHYSICS.softening * PHYSICS.softening;

    for (let i = 0; i < balls.length; i += 1) {
      const a = balls[i];

      for (let j = i + 1; j < balls.length; j += 1) {
        const b = balls[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let distanceSq = dx * dx + dy * dy;

        if (distanceSq < 0.0001) {
          dx = (j - i) * 0.01;
          dy = 0.01;
          distanceSq = dx * dx + dy * dy;
        }

        const distance = Math.sqrt(distanceSq);
        const nx = dx / distance;
        const ny = dy / distance;
        const direction = a.charge === b.charge ? -1 : 1;
        const forceMagnitude = this.forceStrength / (distanceSq + softeningSq);
        const forceX = nx * forceMagnitude * direction;
        const forceY = ny * forceMagnitude * direction;

        deltaVx[i] += forceX * dt;
        deltaVy[i] += forceY * dt;
        deltaVx[j] -= forceX * dt;
        deltaVy[j] -= forceY * dt;
      }
    }

    const dampingFactor = Math.exp(-PHYSICS.damping * dt);
    for (let i = 0; i < balls.length; i += 1) {
      const ball = balls[i];
      ball.vx = (ball.vx + deltaVx[i]) * dampingFactor;
      ball.vy = (ball.vy + deltaVy[i]) * dampingFactor;

      const speed = Math.hypot(ball.vx, ball.vy);
      if (speed > PHYSICS.maxSpeed) {
        const scale = PHYSICS.maxSpeed / speed;
        ball.vx *= scale;
        ball.vy *= scale;
      }
    }
  }

  private integratePositions(dt: number): void {
    this.integratePositionsForBalls(this.balls, {
      traceWallEvents: true,
      collidedBallIds: this.collidedBallIds,
    }, dt);
  }

  private integratePositionsForBalls(
    balls: BallState[],
    options: {
      traceWallEvents: boolean;
      collidedBallIds: Set<number>;
    },
    dt: number,
  ): void {
    const minX = this.arena.x + BALL_RADIUS;
    const maxX = this.arena.x + this.arena.width - BALL_RADIUS;
    const minY = this.arena.y + BALL_RADIUS;
    const maxY = this.arena.y + this.arena.height - BALL_RADIUS;

    for (const ball of balls) {
      ball.x += ball.vx * dt;
      ball.y += ball.vy * dt;

      if (ball.x < minX) {
        ball.x = minX;
        ball.vx = Math.abs(ball.vx) * this.bounce;
        this.applyWallColor(ball, this.walls.left, "left", balls, options);
      } else if (ball.x > maxX) {
        ball.x = maxX;
        ball.vx = -Math.abs(ball.vx) * this.bounce;
        this.applyWallColor(ball, this.walls.right, "right", balls, options);
      }

      if (ball.y < minY) {
        ball.y = minY;
        ball.vy = Math.abs(ball.vy) * this.bounce;
        this.applyWallColor(ball, this.walls.top, "top", balls, options);
      } else if (ball.y > maxY) {
        ball.y = maxY;
        ball.vy = -Math.abs(ball.vy) * this.bounce;
        this.applyWallColor(ball, this.walls.bottom, "bottom", balls, options);
      }
    }
  }

  private applyWallColor(
    ball: BallState,
    wallColor: WallColor,
    wallSide: "left" | "right" | "top" | "bottom",
    balls: BallState[],
    options: {
      traceWallEvents: boolean;
      collidedBallIds: Set<number>;
    },
  ): void {
    const blockedByCollisionSet = options.collidedBallIds.has(ball.id);
    const blockedByCurrentCollision = this.isBallCollidingIn(balls, ball.id);
    if (blockedByCollisionSet || blockedByCurrentCollision) {
      if (options.traceWallEvents) {
        tracePolarity("wall_color_skipped", {
          stepCount: this.stepCount,
          ballId: ball.id,
          wallSide,
          wallColor,
          charge: ball.charge,
          reason: blockedByCollisionSet ? "collided_set" : "currently_colliding",
          x: round(ball.x),
          y: round(ball.y),
        });
      }
      return;
    }

    const fromCharge = ball.charge;
    const charge = wallColorToCharge(wallColor);
    if (charge !== null) {
      ball.charge = charge;
    }
    if (options.traceWallEvents) {
      tracePolarity("wall_bounce", {
        stepCount: this.stepCount,
        ballId: ball.id,
        wallSide,
        wallColor,
        fromCharge,
        toCharge: ball.charge,
        changedCharge: fromCharge !== ball.charge,
        x: round(ball.x),
        y: round(ball.y),
      });
    }
  }

  private resolvePendingCollision(source: "step" | "flip_guard"): boolean {
    const collisions = this.collectCollisionPairs();
    if (collisions.length > 0) {
      this.collidedBallIds = new Set(
        collisions.flatMap((collision) => [collision.aId, collision.bId]),
      );
      this.status = "lost";
      this.currentLevelElapsedMs = 0;
      this.currentLevelScoreTransferable = false;
      tracePolarity("collision_detected", {
        stepCount: this.stepCount,
        source,
        collisions: collisions.map((collision) => ({
          ...collision,
          distance: round(collision.distance),
          threshold: round(collision.threshold),
        })),
      });
      return true;
    }

    return false;
  }

  private isBallColliding(ballId: number): boolean {
    return this.collectCollisionPairsIn(this.balls).some(
      (collision) => collision.aId === ballId || collision.bId === ballId,
    );
  }

  private isBallCollidingIn(balls: BallState[], ballId: number): boolean {
    return this.collectCollisionPairsIn(balls).some(
      (collision) => collision.aId === ballId || collision.bId === ballId,
    );
  }

  private collectCollisionPairs(): CollisionPair[] {
    return this.collectCollisionPairsIn(this.balls);
  }

  private collectCollisionPairsIn(balls: BallState[]): CollisionPair[] {
    const collisions: CollisionPair[] = [];

    for (let i = 0; i < balls.length; i += 1) {
      const a = balls[i];
      for (let j = i + 1; j < balls.length; j += 1) {
        const b = balls[j];
        const radius = a.radius + b.radius;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const distanceSq = dx * dx + dy * dy;
        if (distanceSq <= radius * radius) {
          collisions.push({
            aId: a.id,
            bId: b.id,
            aCharge: a.charge,
            bCharge: b.charge,
            aX: round(a.x),
            aY: round(a.y),
            bX: round(b.x),
            bY: round(b.y),
            distance: Math.sqrt(distanceSq),
            threshold: radius,
          });
        }
      }
    }

    return collisions;
  }

  private predictImminentCollisionAfterFlip(
    index: number,
    clickedBallId: number,
  ): { lookaheadSteps: number; collisions: CollisionPair[] } | null {
    const predictedBalls = this.balls.map((ball) => ({ ...ball }));
    const predictedBall = predictedBalls[index];
    if (!predictedBall) {
      return null;
    }

    predictedBall.charge = invertCharge(predictedBall.charge);
    const dt = PHYSICS.fixedStepMs / 1000;

    for (let lookaheadSteps = 1; lookaheadSteps <= FLIP_COLLISION_GUARD_STEPS; lookaheadSteps += 1) {
      this.applyForcesToBalls(predictedBalls, dt);
      this.integratePositionsForBalls(
        predictedBalls,
        {
          traceWallEvents: false,
          collidedBallIds: new Set<number>(),
        },
        dt,
      );
      const collisions = this.collectCollisionPairsIn(predictedBalls).filter(
        (collision) =>
          collision.aId === clickedBallId || collision.bId === clickedBallId,
      );
      if (collisions.length > 0) {
        return { lookaheadSteps, collisions };
      }
    }

    return null;
  }

  private buildScoreSnapshot(): SimulationSnapshot["score"] {
    const currentLevelPoints = this.getCurrentLevelScorePoints();
    const earnedTotalPoints = this.carriedScorePoints + currentLevelPoints;
    return {
      totalPoints: Math.max(0, earnedTotalPoints - this.spentScorePoints),
      earnedTotalPoints,
      carriedPoints: this.carriedScorePoints,
      currentLevelPoints,
      spentPoints: this.spentScorePoints,
      pointsPerSecond: SCORE_POINTS_PER_SECOND,
    };
  }

  private buildPerksSnapshot(score: SimulationSnapshot["score"]): SimulationSnapshot["perks"] {
    return {
      shop: PERK_DEFINITIONS.map((perk) => ({
        id: perk.id,
        name: perk.name,
        sign: perk.sign,
        description: perk.description,
        cost: perk.cost,
        ownedCount: this.perkInventory[perk.id],
        affordable: score.totalPoints >= perk.cost,
        canPurchase: canPurchasePerkInStatus(this.status) && score.totalPoints >= perk.cost,
      })),
      inventory: PERK_DEFINITIONS.map((perk) => {
        const disabledReason = this.getPerkDisabledReason(perk.id);
        return {
          id: perk.id,
          name: perk.name,
          sign: perk.sign,
          count: this.perkInventory[perk.id],
          canActivate: disabledReason === null,
          disabledReason,
        };
      }),
      activeEffects: this.buildActivePerkEffectsSnapshot(),
    };
  }

  private buildActivePerkEffectsSnapshot(): SimulationSnapshot["perks"]["activeEffects"] {
    return {
      freezeBallsRemainingMs: Math.ceil(
        Math.max(0, this.freezeBallsRemainingMs - TIME_FLOAT_TOLERANCE_MS),
      ),
      wallsNeutralized: this.wallsNeutralizedForAttempt,
    };
  }

  private getPerkDisabledReason(perkId: PerkId): string | null {
    if (this.status !== "running" && this.status !== "paused") {
      return "level is not active";
    }

    if (this.perkInventory[perkId] <= 0) {
      return "not owned";
    }

    if (perkId === "remove_random_ball" && this.balls.length <= 1) {
      return "only one ball remains";
    }

    if (perkId === "neutralize_walls" && this.areWallsNeutral()) {
      return "walls are already neutral";
    }

    return null;
  }

  private areWallsNeutral(): boolean {
    return (
      this.walls.left === "gray" &&
      this.walls.right === "gray" &&
      this.walls.top === "gray" &&
      this.walls.bottom === "gray"
    );
  }

  private nextPerkRandomUnit(): number {
    this.perkRandomState = (Math.imul(this.perkRandomState, 1664525) + 1013904223) >>> 0;
    return this.perkRandomState / 0x1_0000_0000;
  }

  private getCurrentLevelScorePoints(): number {
    const elapsedMs = Math.min(this.currentLevelElapsedMs, this.levelDurationMs);
    const fullSeconds = Math.floor((elapsedMs + TIME_FLOAT_TOLERANCE_MS) / 1000);
    return fullSeconds * SCORE_POINTS_PER_SECOND;
  }

}

export interface PerkShopItemSnapshot {
  id: PerkId;
  name: string;
  sign: string;
  description: string;
  cost: number;
  ownedCount: number;
  affordable: boolean;
  canPurchase: boolean;
}

export interface PerkInventoryItemSnapshot {
  id: PerkId;
  name: string;
  sign: string;
  count: number;
  canActivate: boolean;
  disabledReason: string | null;
}

function buildWallSnapshot(color: WallColor): { charge: Charge | null; color: WallColor } {
  return {
    charge: wallColorToCharge(color),
    color,
  };
}

function createEmptyPerkInventory(): Record<PerkId, number> {
  return PERK_DEFINITIONS.reduce(
    (inventory, perk) => {
      inventory[perk.id] = 0;
      return inventory;
    },
    {} as Record<PerkId, number>,
  );
}

function createPerkSeed(level: LevelReference): number {
  try {
    return seedForLevel(level);
  } catch {
    let hash = 2166136261;
    for (const character of String(level)) {
      hash ^= character.charCodeAt(0);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0 || 1;
  }
}

function isValidAdminScoreAmount(amount: number): boolean {
  return Number.isSafeInteger(amount) && amount > 0;
}

function canPurchasePerkInStatus(status: SimulationStatus): boolean {
  return status === "paused" || status === "won" || status === "lost";
}

function invertCharge(charge: Charge): Charge {
  return charge === 1 ? -1 : 1;
}

function wallColorToCharge(color: WallColor): Charge | null {
  switch (color) {
    case "red":
      return 1;
    case "blue":
      return -1;
    case "gray":
      return null;
  }
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function roundToHundredths(value: number): number {
  return Math.round(value * 100) / 100;
}
